import type { Address, Party } from '@my/directory-api';
import type {
  OrderBooking,
  OrderTask,
  TaskStateValue,
  TaskTimeModeValue,
  TaskTypeValue,
} from '@my/tms-api';
import type { MockBooking, MockTask, TaskState, TaskType, TimeMode } from './types';

/**
 * BE `TaskTypeValue` → local display `TaskType`. The BE enum has ~24
 * specialty types; the display union collapses them into three "process
 * event" buckets so the badge layer doesn't enumerate every variant.
 * Container's PickUp / DropOff and the Pallet family's Collect /
 * Deliver pass through unchanged — they have distinct semantics from
 * each other and earlier renames collided badly.
 */
export function adaptTaskType(t: TaskTypeValue): TaskType {
  switch (t) {
    case 'PickUp':
    case 'DropOff':
    case 'BookingDeparture':
    case 'BookingArrival':
    case 'Collect':
    case 'Load':
    case 'Unload':
    case 'Deliver':
    case 'Stop':
    case 'Instruction':
    case 'Inspection':
      return t;
    case 'FumigationStop':
    case 'PermitStop':
    case 'FeedWaterStop':
      return 'Stop';
    case 'TempCheck':
    case 'ReeferInspection':
    case 'SampleCheck':
    case 'Weigh':
    case 'Wash':
    case 'TankClean':
    case 'VetCheck':
    case 'BorderInspection':
    case 'PhytosanitaryCheck':
    case 'SiteCheck':
      return 'Inspection';
    case 'EscortStart':
      return 'Instruction';
    default:
      // New BE types added without an adapter update — surface as a
      // generic process event rather than masquerading as a PickUp.
      return 'Stop';
  }
}

/** BE `Any` collapses to local `Undefined` so the time-mode pill row always has a target. */
export function adaptTimeMode(tm: TaskTimeModeValue | null | undefined): TimeMode {
  if (!tm || tm === 'Any') return 'Undefined';
  return tm;
}

/** BE `Cancelled` collapses to local `Pending` (display union doesn't carry Cancelled yet). */
export function adaptTaskState(s: TaskStateValue): TaskState {
  if (s === 'Cancelled') return 'Pending';
  return s;
}

export function adaptOrderTaskToMock(task: OrderTask, address: Address | undefined): MockTask {
  const title = address?.name?.trim() || 'Unknown site';
  const subtitleParts = [address?.city, address?.countryIsoCode].filter(
    (s): s is string => !!s && s.trim().length > 0,
  );
  const subtitle = subtitleParts.join(', ');

  return {
    id: task.id,
    type: adaptTaskType(task.type),
    title,
    subtitle,
    countryCode: address?.countryIsoCode ?? undefined,
    bookingId: task.bookingId ?? undefined,
    plannedDate: task.plannedDate ?? undefined,
    plannedTime: task.plannedTime ?? undefined,
    plannedDepartureTime: task.plannedDepartureTime ?? undefined,
    timeMode: adaptTimeMode(task.timeMode),
    // Actuals aren't wired yet — driver-app event capture lands later.
    actualDate: undefined,
    actualArrivalTime: undefined,
    actualDepartureTime: undefined,
    notes: task.notes ?? undefined,
    state: adaptTaskState(task.state),
    statusText: task.statusText ?? '',
    progressDetail: undefined,
    _source: task,
  };
}

/**
 * Adapt a wire `OrderBooking` into the local `MockBooking`. The carrier
 * label resolves from `carrierPartyId` against the org's Supplier
 * parties (auto-created from Settings → Carriers & Routes, so the
 * party name IS the catalogue carrier name). Falls back to routeName
 * when the party query hasn't landed yet or the booking pre-dates a
 * carrier pick.
 */
export function adaptOrderBookingToMock(
  booking: OrderBooking,
  supplierByPartyId?: Map<string, Party>,
): MockBooking {
  const carrierId = booking.carrierPartyId ?? null;
  const agentId = booking.agentPartyId ?? null;
  const resolvedCarrier =
    (carrierId && supplierByPartyId?.get(carrierId)?.name) || booking.routeName || 'Carrier';
  const resolvedAgent = (agentId && supplierByPartyId?.get(agentId)?.name) || null;
  return {
    id: booking.id,
    routeId: booking.carrierRouteId ?? '',
    routeCode: booking.routeCode ?? '',
    routeName: booking.routeName ?? '',
    carrier: resolvedCarrier,
    agent: resolvedAgent,
    reference: booking.carrierReference ?? '',
    vessel: booking.vessel ?? undefined,
    voyageNumber: booking.voyageNumber ?? undefined,
    sailDate: booking.sailDate ?? undefined,
    sailTime: booking.sailTime ?? undefined,
    notes: booking.notes ?? undefined,
    _source: booking,
  };
}

/** Look up the Booking metadata for a task if it's one half of a booking pair. */
export function bookingFor(task: MockTask, bookings: MockBooking[]): MockBooking | null {
  if (!task.bookingId) return null;
  return bookings.find((b) => b.id === task.bookingId) ?? null;
}
